// src/pages/sitegroup/SiteGroupsPage.js
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import { getSiteGroups, deleteSiteGroup } from '../../api/siteGroupAPI';
import { getCompanies } from '../../api/companyAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const SiteGroupsPage = () => {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteGroupToDelete, setSiteGroupToDelete] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [showEnabledOnly, setShowEnabledOnly] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch companies for filter dropdown
  const { data: companies, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getCompanies()
  });

  // Set default company ID when companies are loaded
  useEffect(() => {
    if (companies && companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].CompanyId.toString());
    }
  }, [companies, selectedCompanyId]);

  // Fetch site groups based on selected company and enabled filter
  const { 
    data: siteGroups, 
    isLoading: isLoadingSiteGroups, 
    isError: isSiteGroupsError, 
    error: siteGroupsError 
  } = useQuery({
    queryKey: ['site-groups', selectedCompanyId, showEnabledOnly],
    queryFn: () => {
      if (selectedCompanyId) {
        return getSiteGroups(
          parseInt(selectedCompanyId), 
          showEnabledOnly ? true : undefined
        );
      }
      return [];
    },
    enabled: !!selectedCompanyId && !isLoadingCompanies
  });

  // Delete site group mutation
  const deleteMutation = useMutation({
    mutationFn: deleteSiteGroup,
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['site-groups'] });
      
      setSnackbar({
        open: true,
        message: 'Site group deleted successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting site group: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleDeleteClick = (siteGroup) => {
    setSiteGroupToDelete({
      id: siteGroup.SiteGroupId,
      name: siteGroup.SiteGroupName
    });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (siteGroupToDelete) {
      try {
        await deleteMutation.mutateAsync(siteGroupToDelete.id);
      } catch (err) {
        // Error is handled by the mutation
      }
      setDeleteDialogOpen(false);
      setSiteGroupToDelete(null);
    }
  };

  const handleCompanyChange = (event) => {
    setSelectedCompanyId(event.target.value);
  };

  // Toggle handler for enabled/all site groups
  const handleToggleEnabled = () => {
    setShowEnabledOnly(!showEnabledOnly);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Find the current company name
  const currentCompany = companies?.find(company => company.CompanyId.toString() === selectedCompanyId);
  const currentCompanyName = currentCompany?.CompanyName || "Unknown Company";

  if (isLoadingCompanies || isLoadingSiteGroups) {
    return <LoadingSpinner />;
  }

  if (isSiteGroupsError) {
    return <ErrorAlert message={siteGroupsError instanceof Error ? siteGroupsError.message : 'Failed to load site groups'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Site Groups</Typography>
        <Button 
          component={Link} 
          to={`/site-groups/new${selectedCompanyId ? `?company=${selectedCompanyId}` : ''}`}
          variant="contained" 
          startIcon={<AddIcon />}
        >
          Add Site Group
        </Button>
      </Box>

      {/* Company Filter and Toggle */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="company-filter-label">Company</InputLabel>
              <Select
                labelId="company-filter-label"
                value={selectedCompanyId}
                label="Company"
                onChange={handleCompanyChange}
                startAdornment={
                  <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }
              >
                {!isLoadingCompanies && companies && companies.map((company) => (
                  <MenuItem key={company.CompanyId} value={company.CompanyId.toString()}>
                    {company.CompanyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={showEnabledOnly}
                  onChange={handleToggleEnabled}
                  color="primary"
                />
              }
              label="Show enabled site groups only"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>
          Site Groups for: {currentCompanyName}
        </Typography>

        {siteGroups && siteGroups.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No site groups found for this company
            </Typography>
            <Button 
              component={Link} 
              to={`/site-groups/new?company=${selectedCompanyId}`}
              variant="contained" 
              startIcon={<AddIcon />}
              sx={{ mt: 2 }}
            >
              Add Your First Site Group
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {siteGroups && siteGroups.map((siteGroup) => (
                  <TableRow key={siteGroup.SiteGroupId}>
                    <TableCell>{siteGroup.SiteGroupId}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        {siteGroup.SiteGroupName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={siteGroup.SiteGroupEnabled ? "Enabled" : "Disabled"} 
                        color={siteGroup.SiteGroupEnabled ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(siteGroup.SiteGroupCreated).toLocaleString('en-GB')}
                    </TableCell>
                    <TableCell>
                      {new Date(siteGroup.SiteGroupUpdated).toLocaleString('en-GB')}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        component={Link}
                        to={`/site-groups/${siteGroup.SiteGroupId}/edit`}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(siteGroup)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Site Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{siteGroupToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SiteGroupsPage;